/**
 * The task form, without the form: what it starts with, what is wrong with it,
 * and what it sends. Pure, so the rule that matters most — AN EDIT SENDS ONLY
 * WHAT WAS CHANGED — is tested as a function.
 *
 * That rule is not tidiness. The server works out who to tell from what a save
 * changes (apps/api/src/services/taskChanges.ts); a form that re-sent every
 * field would be harmless, but one that re-sent a STALE field — the description
 * as it was when the dialog opened, over an edit somebody made since — would
 * quietly undo it. Leaving a key out is the only way to say "I did not touch
 * this".
 */
import type {
  CreateTaskRequest,
  Task,
  TaskChecklistItem,
  TaskPriority,
  UpdateTaskRequest,
} from '@cyphex/types/admin';
import type { ConversationId, MessageId, UserId } from '@cyphex/types/common';
import type { UserRef } from '@cyphex/types/identity';

import { fromLocalInput, toLocalInput } from './taskView';

export const TITLE_MAX = 300;
export const DESCRIPTION_MAX = 8000;
export const PEOPLE_MAX = 50;
export const CHECKLIST_MAX = 100;

export interface TaskFormValues {
  title: string;
  description: string;
  assignees: readonly UserRef[];
  /** CC. */
  watchers: readonly UserRef[];
  /** The value of a `datetime-local` input; empty for no date. */
  due: string;
  priority: TaskPriority;
  checklist: readonly TaskChecklistItem[];
}

/** What a NEW task starts with. A task made from a message starts with its words. */
export interface TaskFormSeed {
  title?: string;
  description?: string;
  assignees?: readonly UserRef[];
  /** The message it was made from, so the task can link back to it. */
  source?: { messageId: string; conversationId: string };
}

/** The longest title a message is cut down to. The whole text goes in the description. */
export const SEED_TITLE_MAX = 120;

/**
 * A task made from a message: its first line is the title, and when that is not
 * the whole message, the message is the description. Nothing is assigned — the
 * message may be somebody asking the reader, or the reader asking somebody.
 */
export function seedFromMessage(
  text: string | null,
  source: { messageId: string; conversationId: string },
): TaskFormSeed {
  const whole = (text ?? '').trim();
  const firstLine = (whole.split(/\r?\n/).find((line) => line.trim() !== '') ?? '').trim();
  const title =
    firstLine.length <= SEED_TITLE_MAX
      ? firstLine
      : `${firstLine.slice(0, SEED_TITLE_MAX - 1).trimEnd()}…`;
  return {
    title,
    ...(whole === title ? {} : { description: whole.slice(0, DESCRIPTION_MAX) }),
    source,
  };
}

export const emptyForm = (): TaskFormValues => ({
  title: '',
  description: '',
  assignees: [],
  watchers: [],
  due: '',
  priority: 'normal',
  checklist: [],
});

export const formFromTask = (task: Task): TaskFormValues => ({
  title: task.title,
  description: task.description ?? '',
  assignees: task.assignees,
  watchers: task.watchers,
  due: toLocalInput(task.dueAt),
  priority: task.priority,
  checklist: task.checklist,
});

/** What stops the form being saved, in words — or null. */
export function formProblem(values: TaskFormValues): string | null {
  if (values.title.trim() === '') return 'Give the task a title.';
  if (values.title.trim().length > TITLE_MAX) return 'That title is too long.';
  if (values.description.length > DESCRIPTION_MAX) return 'That description is too long.';
  if (values.due !== '' && fromLocalInput(values.due) === null) {
    return 'That due date is not a date.';
  }
  if (values.checklist.some((item) => item.text.trim() === '')) {
    return 'A checklist step is empty. Fill it in or remove it.';
  }
  return null;
}

/**
 * An id for a checklist step added in the form. The smallest `n<k>` not in
 * use: no clock and no dice, and it cannot collide with the `c<k>` the server
 * gives the steps a task is created with.
 */
export function nextItemId(items: readonly TaskChecklistItem[]): string {
  const used = new Set(items.map((item) => item.id));
  let k = 1;
  while (used.has(`n${String(k)}`)) k += 1;
  return `n${String(k)}`;
}

const ids = (people: readonly UserRef[]): UserId[] => people.map((person) => person.id);

/**
 * CC never repeats an assignee: somebody doing the task is more than copied on
 * it. The server drops them too; doing it here keeps the form honest about what
 * will be saved.
 */
export function ccWithout(
  watchers: readonly UserRef[],
  assignees: readonly UserRef[],
): readonly UserRef[] {
  const doing = new Set<string>(ids(assignees));
  return watchers.filter((person) => !doing.has(person.id));
}

export function toCreateRequest(
  values: TaskFormValues,
  scope: { kind: 'channel'; id: string } | null,
  source: { messageId: string; conversationId: string } | null = null,
): CreateTaskRequest {
  const due = fromLocalInput(values.due);
  const description = values.description.trim();
  const steps = values.checklist.map((item) => ({ text: item.text.trim() }));
  const cc = ccWithout(values.watchers, values.assignees);
  return {
    title: values.title.trim(),
    ...(description === '' ? {} : { description: values.description }),
    // Nobody chosen: the server makes it the caller's own.
    ...(values.assignees.length === 0 ? {} : { assigneeIds: ids(values.assignees) }),
    ...(cc.length === 0 ? {} : { watcherIds: ids(cc) }),
    ...(due === null ? {} : { dueAt: due }),
    ...(values.priority === 'normal' ? {} : { priority: values.priority }),
    ...(steps.length === 0 ? {} : { checklist: steps }),
    ...(scope === null ? {} : { scopeKind: scope.kind, scopeId: scope.id }),
    ...(source === null
      ? {}
      : {
          // Branded on the wire contract, plain strings here: this module has
          // no runtime imports, which is what lets it be tested as a function.
          sourceMessageId: source.messageId as MessageId,
          sourceConversationId: source.conversationId as ConversationId,
        }),
  };
}

const sameList = (a: readonly string[], b: readonly string[]): boolean =>
  a.length === b.length && a.every((value, index) => value === b[index]);

const sameSet = (a: readonly string[], b: readonly string[]): boolean =>
  a.length === b.length && a.every((value) => b.includes(value));

const sameChecklist = (a: readonly TaskChecklistItem[], b: readonly TaskChecklistItem[]): boolean =>
  a.length === b.length &&
  a.every((item, index) => {
    const other = b[index];
    return (
      other !== undefined &&
      item.id === other.id &&
      item.text === other.text &&
      item.done === other.done
    );
  });

/** Only what differs from the task as it was when the form opened. */
export function toUpdateRequest(task: Task, values: TaskFormValues): UpdateTaskRequest {
  const out: UpdateTaskRequest = {};

  const title = values.title.trim();
  if (title !== task.title) out.title = title;

  const description = values.description.trim() === '' ? null : values.description;
  if (description !== (task.description ?? null)) out.description = description;

  if (values.priority !== task.priority) out.priority = values.priority;

  // Compared as the input shows them: to the minute. The stored instant has
  // seconds the input cannot express, and would otherwise always look changed.
  if (values.due !== toLocalInput(task.dueAt)) out.dueAt = fromLocalInput(values.due);

  const assignees = ids(values.assignees);
  if (!sameList(assignees, ids(task.assignees))) out.assigneeIds = assignees;

  const cc = ids(ccWithout(values.watchers, values.assignees));
  if (!sameSet(cc, ids(task.watchers))) out.watcherIds = cc;

  const checklist = values.checklist.map((item) => ({ ...item, text: item.text.trim() }));
  if (!sameChecklist(checklist, task.checklist)) out.checklist = checklist;

  return out;
}

export const hasChanges = (request: UpdateTaskRequest): boolean => Object.keys(request).length > 0;
